import type {ReactElement} from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {IconWithTooltip} from '@Components/icon-with-tooltip/icon-with-tooltip';
import {ElementTestId} from '@Tests/tests.types';
import {useAppSelector} from '@/hooks';
import {getPagePlaybackIcon, isPagePlaying, isPagePlaybackEnded} from '@Components/poster-editor/poster-editor-reducer';
import {onPosterPause} from '@Components/poster-editor/library/poster-editor-library';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

interface PagePlayPauseButtonProps {
  pageId: string;
}

export function PagePlayPauseButton(props: PagePlayPauseButtonProps): ReactElement | null {
  const isPlaying = useAppSelector((state) => isPagePlaying(state, props.pageId));
  const isEnded = useAppSelector((state) => isPagePlaybackEnded(state, props.pageId));
  const icon = useAppSelector((state) => getPagePlaybackIcon(state, props.pageId));

  const onPlayPauseClick = (): void => {
    if (!isPlaying) {
      trackPosterBuilderGA4Events(GA4EventName.PLAY_PAGE);
      void window.posterEditor?.whiteboard?.play(props.pageId);
    } else {
      onPosterPause();
    }
  };

  const getTooltipText = (): string => {
    if (isPlaying) {
      return window.i18next.t('pmwjs_pause_page');
    }
    if (isEnded) {
      return window.i18next.t('pmwjs_replay_page');
    }
    return window.i18next.t('pmwjs_play_page');
  };

  return (
    <IconWithTooltip
      onClick={onPlayPauseClick}
      icon={icon}
      type={IconType.GHOST_UNCOLORED}
      size={IconSize.SIZE_ICON_16}
      shape={IconShape.SQUARE}
      data-testid={ElementTestId.POSTER_EDITOR_WEB_BOTTOM_BAR_PLAY_PAUSE_BUTTON}
      tooltipText={getTooltipText()}
    />
  );
}
